import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, ReceiptText } from "lucide-react";
import { ApiV2Error } from "../../lib/api-v2";
import { credits, rupees } from "../products/types";
import { formatWhen } from "./format-when";
import { OrderStatusBadge } from "./order-status-badge";
import { useOrders } from "./queries";

const errorMessage = (err: unknown, fallback: string) => (err instanceof ApiV2Error ? err.message : fallback);

const itemsLine = (names: string[]) =>
  names.length <= 2 ? names.join(", ") : `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;

/** /orders — the account's orders, newest first. */
export function OrderHistoryPage() {
  const orders = useOrders();
  const all = useMemo(() => orders.data?.pages.flatMap((p) => p.items) ?? [], [orders.data]);

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-8">
      <header>
        <Link to="/products" className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-2 hover:text-brand">
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-ink">Order history</h1>
        <p className="mt-1.5 text-sm text-ink-2">Every order placed from this account, newest first.</p>
      </header>

      {orders.isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-20 animate-pulse rounded-2xl bg-soft" />)}
        </div>
      ) : orders.isError ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-line bg-elevated p-12 text-center">
          <p className="text-sm text-danger">{errorMessage(orders.error, "Could not load your orders.")}</p>
          <button onClick={() => { void orders.refetch(); }} className="rounded-lg border border-line px-4 py-2 text-sm font-semibold text-ink hover:bg-soft">
            Retry
          </button>
        </div>
      ) : all.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-line bg-elevated px-6 py-16 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-soft text-brand">
            <ReceiptText className="h-6 w-6" />
          </span>
          <p className="text-base font-semibold text-ink">No orders yet</p>
          <p className="max-w-sm text-sm text-ink-2">Orders appear here once you pay for a cart.</p>
          <Link to="/products" className="mt-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover">
            Browse products
          </Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {all.map((order) => (
            <li key={order.id}>
              <Link
                to={`/orders/${order.id}`}
                aria-label={`Order ${order.reference_number}`}
                className="flex items-center gap-4 rounded-2xl border border-line bg-elevated p-5 shadow-sm transition-colors hover:border-brand/40 hover:bg-soft/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-ink">{order.reference_number}</span>
                    <OrderStatusBadge status={order.status} />
                  </div>
                  <p className="mt-1 truncate text-sm text-ink-2">
                    {itemsLine(order.items.map((i) => i.name))}
                    {order.credits > 0 && ` · ${credits(order.credits)} credits`}
                  </p>
                  <p className="mt-0.5 text-xs text-ink-3">{formatWhen(order.created_at)}</p>
                </div>
                <span className="text-base font-bold tabular-nums text-ink">{rupees(order.totals.total)}</span>
                <ChevronRight className="h-5 w-5 shrink-0 text-ink-3" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {orders.hasNextPage && (
        <div className="text-center">
          <button
            onClick={() => { void orders.fetchNextPage(); }}
            disabled={orders.isFetchingNextPage}
            className="rounded-lg border border-line px-5 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:cursor-wait disabled:text-ink-3"
          >
            {orders.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
